import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Plus, Save, Trash2, Edit, GripVertical, Settings,
  FileText, Mail, Image, Package, UserCheck, Palette,
  ArrowUp, ArrowDown, X, Users, Workflow
} from 'lucide-react';
import UserMultiSelect from './UserMultiSelect';

const ICON_OPTIONS = [
  { value: 'FileText', label: 'Dokument' },
  { value: 'Mail', label: 'E-Mail/Newsletter' },
  { value: 'Image', label: 'Grafik/Bild' },
  { value: 'Package', label: 'Paket/Bestellung' },
  { value: 'UserCheck', label: 'Akkreditierung' },
  { value: 'Settings', label: 'Allgemein' },
];

const FIELD_TYPES = [
  { value: 'short_text', label: 'Kurzer Text' },
  { value: 'long_text', label: 'Langer Text' },
  { value: 'number', label: 'Zahl' },
  { value: 'date', label: 'Datum' },
  { value: 'datetime', label: 'Datum + Uhrzeit' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'checkbox', label: 'Mehrfachauswahl' },
  { value: 'yes_no', label: 'Ja/Nein' },
  { value: 'url', label: 'URL' },
  { value: 'file_upload', label: 'Datei-Upload' },
  { value: 'image_upload', label: 'Bild-Upload' },
  { value: 'pdf_upload', label: 'PDF-Upload' },
  { value: 'event_select', label: 'Spieltag-Auswahl' },
  { value: 'user_select', label: 'User-Auswahl' },
];

const STATUS_CATEGORIES = [
  { value: 'open', label: 'Offen', color: '#3b82f6' },
  { value: 'in_progress', label: 'In Bearbeitung', color: '#f59e0b' },
  { value: 'waiting', label: 'Wartet', color: '#8b5cf6' },
  { value: 'done', label: 'Erledigt', color: '#10b981' },
  { value: 'rejected', label: 'Abgelehnt', color: '#ef4444' },
];

const AdminAnfragen = () => {
  const { user } = useContext(AuthContext);
  const [types, setTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);
  const [activeTab, setActiveTab] = useState('types');

  // Type dialog
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm, setTypeForm] = useState({ name: '', description: '', icon: 'FileText', color: '#6b7280', auto_archive_days: 7 });

  // Form builder
  const [fields, setFields] = useState([]);
  const [showFieldDialog, setShowFieldDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [fieldForm, setFieldForm] = useState({
    type: 'short_text', label: '', placeholder: '', help_text: '',
    required: false, readonly: false, default_value: '', options: [],
    conditions: [],
  });
  const [optionInput, setOptionInput] = useState('');

  // Workflow / Status
  const [statuses, setStatuses] = useState([]);
  const [defaultStatusId, setDefaultStatusId] = useState('');
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [statusForm, setStatusForm] = useState({ name: '', color: '#6b7280', category: 'open' });
  const [editingStatus, setEditingStatus] = useState(null);

  // Workflow steps (approval flow)
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [showStepDialog, setShowStepDialog] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [stepForm, setStepForm] = useState({ name: '', approver_ids: [], target_status_id: '', rejection_status_id: '', pending_status_id: '' });

  // Recipients
  const [recipients, setRecipients] = useState([]);

  useEffect(() => { fetchTypes(); fetchUsers(); }, []);

  const fetchTypes = async () => {
    try {
      const res = await axios.get('/anfragen/types');
      setTypes(res.data);
    } catch (err) { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try { setUsers((await axios.get('/anfragen/users')).data); } catch {}
  };

  const selectType = async (t) => {
    setSelectedType(t);
    setFields(t.fields || []);
    setStatuses(t.statuses || []);
    setDefaultStatusId(t.default_status_id || '');
    setRecipients(t.recipients || []);
    setWorkflowSteps(t.workflow_steps || []);
    setActiveTab('form');
  };

  // === TYPE CRUD ===
  const saveType = async () => {
    if (!typeForm.name.trim()) { toast.error('Name erforderlich'); return; }
    try {
      if (editingType) {
        await axios.patch(`/anfragen/types/${editingType.id}`, typeForm);
        toast.success('Anfrageart aktualisiert');
      } else {
        const res = await axios.post('/anfragen/types', typeForm);
        toast.success('Anfrageart erstellt');
        selectType(res.data);
      }
      setShowTypeDialog(false);
      fetchTypes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const deleteType = async (t) => {
    if (!window.confirm(`"${t.name}" wirklich löschen?`)) return;
    try {
      await axios.delete(`/anfragen/types/${t.id}`);
      toast.success('Gelöscht');
      if (selectedType?.id === t.id) setSelectedType(null);
      fetchTypes();
    } catch (err) { toast.error('Fehler beim Löschen'); }
  };

  // === FORM BUILDER ===
  const saveForm = async () => {
    if (!selectedType) return;
    try {
      const res = await axios.put(`/anfragen/types/${selectedType.id}/form`, { fields });
      setFields(res.data.fields);
      toast.success('Formular gespeichert');
      fetchTypes();
    } catch (err) { toast.error('Fehler beim Speichern'); }
  };

  const addField = () => {
    setEditingField(null);
    setFieldForm({ type: 'short_text', label: '', placeholder: '', help_text: '', required: false, readonly: false, default_value: '', options: [], conditions: [] });
    setOptionInput('');
    setShowFieldDialog(true);
  };

  const editField = (f, idx) => {
    setEditingField(idx);
    setFieldForm({ ...f, options: f.options || [], conditions: f.conditions || [] });
    setOptionInput('');
    setShowFieldDialog(true);
  };

  const saveField = () => {
    if (!fieldForm.label.trim()) { toast.error('Feldname erforderlich'); return; }
    const newField = { ...fieldForm, id: editingField !== null ? fields[editingField]?.id : undefined };
    if (editingField !== null) {
      const updated = [...fields];
      updated[editingField] = newField;
      setFields(updated);
    } else {
      setFields([...fields, newField]);
    }
    setShowFieldDialog(false);
  };

  const removeField = (idx) => { setFields(fields.filter((_, i) => i !== idx)); };

  const moveField = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= fields.length) return;
    const updated = [...fields];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    setFields(updated);
  };

  const addOption = () => {
    if (!optionInput.trim()) return;
    setFieldForm(p => ({ ...p, options: [...p.options, optionInput.trim()] }));
    setOptionInput('');
  };

  // === WORKFLOW ===
  const saveWorkflow = async () => {
    if (!selectedType) return;
    try {
      await axios.put(`/anfragen/types/${selectedType.id}/workflow`, {
        statuses,
        default_status_id: defaultStatusId,
        steps: workflowSteps,
      });
      toast.success('Workflow gespeichert');
      fetchTypes();
    } catch (err) { toast.error('Fehler'); }
  };

  const addStep = () => {
    setEditingStep(null);
    setStepForm({ name: '', approver_ids: [], target_status_id: '', rejection_status_id: '', pending_status_id: '' });
    setShowStepDialog(true);
  };

  const editStep = (s, idx) => {
    setEditingStep(idx);
    setStepForm({
      name: s.name || '',
      approver_ids: s.approver_ids || [],
      target_status_id: s.target_status_id || '',
      rejection_status_id: s.rejection_status_id || '',
      pending_status_id: s.pending_status_id || '',
    });
    setShowStepDialog(true);
  };

  const saveStep = () => {
    if (!stepForm.name.trim()) { toast.error('Name erforderlich'); return; }
    if (!stepForm.approver_ids.length) { toast.error('Mindestens 1 Genehmiger wählen'); return; }
    const newStep = {
      id: editingStep !== null ? workflowSteps[editingStep].id : `step_${Date.now()}`,
      ...stepForm,
    };
    if (editingStep !== null) {
      const updated = [...workflowSteps];
      updated[editingStep] = newStep;
      setWorkflowSteps(updated);
    } else {
      setWorkflowSteps([...workflowSteps, newStep]);
    }
    setShowStepDialog(false);
  };

  const removeStep = (idx) => { setWorkflowSteps(workflowSteps.filter((_, i) => i !== idx)); };

  const addStatus = () => {
    setEditingStatus(null);
    setStatusForm({ name: '', color: '#6b7280', category: 'open' });
    setShowStatusDialog(true);
  };

  const saveStatus = () => {
    if (!statusForm.name.trim()) { toast.error('Name erforderlich'); return; }
    if (editingStatus !== null) {
      const updated = [...statuses];
      updated[editingStatus] = { ...updated[editingStatus], ...statusForm };
      setStatuses(updated);
    } else {
      setStatuses([...statuses, { id: `custom_${Date.now()}`, ...statusForm, sort_order: statuses.length }]);
    }
    setShowStatusDialog(false);
  };

  const removeStatus = (idx) => {
    const removed = statuses[idx];
    if (removed.id === defaultStatusId) { toast.error('Standard-Status kann nicht gelöscht werden'); return; }
    setStatuses(statuses.filter((_, i) => i !== idx));
  };

  // === RECIPIENTS ===
  const saveRecipients = async () => {
    if (!selectedType) return;
    try {
      await axios.patch(`/anfragen/types/${selectedType.id}`, { recipients });
      toast.success('Empfänger gespeichert');
      fetchTypes();
    } catch (err) { toast.error('Fehler'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-gray-500">Lade...</div>;

  return (
    <div className="space-y-6" data-testid="admin-anfragen-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Anfrageportal - Verwaltung</h1>
        <p className="text-sm text-gray-500 mt-1">Anfragearten, Formulare und Workflows konfigurieren</p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left: Type List */}
        <div className="col-span-12 lg:col-span-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base">Anfragearten</CardTitle>
              <Button size="sm" onClick={() => { setEditingType(null); setTypeForm({ name: '', description: '', icon: 'FileText', color: '#6b7280', auto_archive_days: 7 }); setShowTypeDialog(true); }} data-testid="add-type-btn">
                <Plus className="w-4 h-4 mr-1" /> Neu
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {types.filter(t => !t.is_deleted).map(t => (
                <div
                  key={t.id}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${selectedType?.id === t.id ? 'bg-[#e30613]/10 border border-[#e30613]/30' : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                  onClick={() => selectType(t)}
                  data-testid={`type-item-${t.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: t.color + '20' }}>
                      <FileText className="w-4 h-4" style={{ color: t.color }} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{t.name}</p>
                      {!t.is_active && <Badge variant="secondary" className="text-[10px]">Inaktiv</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setEditingType(t); setTypeForm({ name: t.name, description: t.description, icon: t.icon, color: t.color, auto_archive_days: t.auto_archive_days ?? 7 }); setShowTypeDialog(true); }}>
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); deleteType(t); }}>
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              {types.length === 0 && <p className="text-sm text-gray-500 text-center py-4">Noch keine Anfragearten</p>}
            </CardContent>
          </Card>
        </div>

        {/* Right: Config Tabs */}
        <div className="col-span-12 lg:col-span-8">
          {selectedType ? (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="form">Formular</TabsTrigger>
                <TabsTrigger value="workflow">Status & Workflow</TabsTrigger>
                <TabsTrigger value="recipients">Empfänger</TabsTrigger>
              </TabsList>

              {/* FORM BUILDER */}
              <TabsContent value="form">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-3">
                    <div>
                      <CardTitle className="text-base">Formular-Builder: {selectedType.name}</CardTitle>
                      <CardDescription>{fields.length} Felder</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={addField} data-testid="add-field-btn">
                        <Plus className="w-4 h-4 mr-1" /> Feld
                      </Button>
                      <Button size="sm" onClick={saveForm} data-testid="save-form-btn">
                        <Save className="w-4 h-4 mr-1" /> Speichern
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {fields.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">Noch keine Felder. Klicke "Feld hinzufügen".</p>
                    ) : (
                      <div className="space-y-2">
                        {fields.map((f, i) => (
                          <div key={f.id || i} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-testid={`field-row-${i}`}>
                            <div className="flex flex-col gap-0.5">
                              <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => moveField(i, -1)} disabled={i === 0}><ArrowUp className="w-3 h-3" /></Button>
                              <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => moveField(i, 1)} disabled={i === fields.length - 1}><ArrowDown className="w-3 h-3" /></Button>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{f.label}</span>
                                {f.required && <Badge variant="destructive" className="text-[10px] px-1">Pflicht</Badge>}
                              </div>
                              <span className="text-xs text-gray-500">{FIELD_TYPES.find(ft => ft.value === f.type)?.label || f.type}</span>
                              {f.options?.length > 0 && <span className="text-xs text-gray-400 ml-2">({f.options.join(', ')})</span>}
                            </div>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => editField(f, i)}><Edit className="w-3 h-3" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeField(i)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* WORKFLOW */}
              <TabsContent value="workflow">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-3">
                    <div>
                      <CardTitle className="text-base">Status & Workflow</CardTitle>
                      <CardDescription>Status-Definitionen für "{selectedType.name}"</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={addStatus} data-testid="add-status-btn"><Plus className="w-4 h-4 mr-1" /> Status</Button>
                      <Button size="sm" onClick={saveWorkflow} data-testid="save-workflow-btn"><Save className="w-4 h-4 mr-1" /> Speichern</Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 mb-4">
                      {statuses.map((s, i) => (
                        <div key={s.id} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-testid={`status-row-${i}`}>
                          <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: s.color }} />
                          <div className="flex-1">
                            <span className="font-medium text-sm">{s.name}</span>
                            <span className="text-xs text-gray-400 ml-2">({STATUS_CATEGORIES.find(c => c.value === s.category)?.label || s.category})</span>
                          </div>
                          {s.id === defaultStatusId && <Badge variant="secondary" className="text-[10px]">Standard</Badge>}
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setDefaultStatusId(s.id)}>Als Standard</Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditingStatus(i); setStatusForm({ name: s.name, color: s.color, category: s.category }); setShowStatusDialog(true); }}><Edit className="w-3 h-3" /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeStatus(i)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                        </div>
                      ))}
                    </div>

                    {/* Workflow Steps (Approval Flow) */}
                    <div className="border-t pt-4 mt-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-sm flex items-center gap-2"><Workflow className="w-4 h-4" /> Freigabe-Schritte</h3>
                          <p className="text-xs text-gray-500">Definieren Sie Genehmigungs-Workflows mit Verantwortlichen</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={addStep} data-testid="add-step-btn">
                          <Plus className="w-4 h-4 mr-1" /> Schritt
                        </Button>
                      </div>
                      {workflowSteps.length === 0 ? (
                        <p className="text-center text-gray-400 text-xs py-4">Keine Freigabe-Schritte konfiguriert</p>
                      ) : (
                        <div className="space-y-2">
                          {workflowSteps.map((s, i) => {
                            const targetStatus = statuses.find(st => st.id === s.target_status_id);
                            return (
                              <div key={s.id} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-testid={`step-row-${i}`}>
                                <div className="w-7 h-7 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center text-xs font-bold text-purple-700 dark:text-purple-300">
                                  {i + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm">{s.name}</p>
                                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5 flex-wrap">
                                    <span>{(s.approver_ids || []).length} Genehmiger</span>
                                    {targetStatus && <><span>→</span><Badge variant="outline" className="text-[10px]" style={{ borderColor: targetStatus.color }}>{targetStatus.name}</Badge></>}
                                  </div>
                                </div>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => editStep(s, i)}><Edit className="w-3 h-3" /></Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeStep(i)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* RECIPIENTS */}
              <TabsContent value="recipients">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-3">
                    <div>
                      <CardTitle className="text-base">Empfänger</CardTitle>
                      <CardDescription>Wer wird bei neuen Anfragen benachrichtigt?</CardDescription>
                    </div>
                    <Button size="sm" onClick={saveRecipients} data-testid="save-recipients-btn"><Save className="w-4 h-4 mr-1" /> Speichern</Button>
                  </CardHeader>
                  <CardContent>
                    <UserMultiSelect
                      users={users}
                      selectedIds={recipients}
                      onChange={setRecipients}
                      placeholder="Nach Name suchen…"
                      testId="recipients-select"
                      emptyText="Keine Personen gefunden"
                    />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          ) : (
            <Card><CardContent className="py-16 text-center text-gray-500">Wähle links eine Anfrageart aus, um sie zu konfigurieren</CardContent></Card>
          )}
        </div>
      </div>

      {/* TYPE DIALOG */}
      <Dialog open={showTypeDialog} onOpenChange={setShowTypeDialog}>
        <DialogContent data-testid="type-dialog">
          <DialogHeader>
            <DialogTitle>{editingType ? 'Anfrageart bearbeiten' : 'Neue Anfrageart'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={typeForm.name} onChange={e => setTypeForm(p => ({ ...p, name: e.target.value }))} data-testid="type-name-input" /></div>
            <div><Label>Beschreibung</Label><Textarea value={typeForm.description} onChange={e => setTypeForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Icon</Label>
                <Select value={typeForm.icon} onValueChange={v => setTypeForm(p => ({ ...p, icon: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ICON_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Farbe</Label>
                <div className="flex gap-2">
                  <Input type="color" value={typeForm.color} onChange={e => setTypeForm(p => ({ ...p, color: e.target.value }))} className="w-12 h-9 p-0.5 cursor-pointer" />
                  <Input value={typeForm.color} onChange={e => setTypeForm(p => ({ ...p, color: e.target.value }))} className="font-mono text-sm" />
                </div>
              </div>
            </div>
            <div>
              <Label>Auto-Archivierung nach (Tage)</Label>
              <p className="text-xs text-gray-400 mb-1.5">Erledigte oder abgelehnte Anfragen werden nach dieser Anzahl von Tagen automatisch archiviert. 0 = deaktiviert.</p>
              <Input type="number" min="0" max="365" value={typeForm.auto_archive_days} onChange={e => setTypeForm(p => ({ ...p, auto_archive_days: parseInt(e.target.value) || 0 }))} className="w-32" data-testid="auto-archive-days-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowTypeDialog(false)}>Abbrechen</Button>
              <Button onClick={saveType} data-testid="save-type-btn"><Save className="w-4 h-4 mr-1" /> Speichern</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* FIELD DIALOG */}
      <Dialog open={showFieldDialog} onOpenChange={setShowFieldDialog}>
        <DialogContent className="max-w-lg" data-testid="field-dialog">
          <DialogHeader><DialogTitle>{editingField !== null ? 'Feld bearbeiten' : 'Neues Feld'}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div><Label>Feldtyp</Label>
              <Select value={fieldForm.type} onValueChange={v => setFieldForm(p => ({ ...p, type: v }))}>
                <SelectTrigger data-testid="field-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FIELD_TYPES.map(ft => <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Label</Label><Input value={fieldForm.label} onChange={e => setFieldForm(p => ({ ...p, label: e.target.value }))} data-testid="field-label-input" /></div>
            <div><Label>Placeholder</Label><Input value={fieldForm.placeholder} onChange={e => setFieldForm(p => ({ ...p, placeholder: e.target.value }))} /></div>
            <div><Label>Hilfetext</Label><Input value={fieldForm.help_text} onChange={e => setFieldForm(p => ({ ...p, help_text: e.target.value }))} /></div>
            <div><Label>Standardwert</Label><Input value={fieldForm.default_value || ''} onChange={e => setFieldForm(p => ({ ...p, default_value: e.target.value }))} /></div>
            <div className="flex gap-4">
              <div className="flex items-center gap-2"><Switch checked={fieldForm.required} onCheckedChange={v => setFieldForm(p => ({ ...p, required: v }))} /><Label>Pflichtfeld</Label></div>
              <div className="flex items-center gap-2"><Switch checked={fieldForm.readonly} onCheckedChange={v => setFieldForm(p => ({ ...p, readonly: v }))} /><Label>Nur lesen</Label></div>
            </div>
            {['dropdown', 'checkbox'].includes(fieldForm.type) && (
              <div>
                <Label>Optionen</Label>
                <div className="space-y-1 mb-2">
                  {fieldForm.options.map((o, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span className="flex-1 p-1.5 bg-gray-50 dark:bg-gray-800 rounded">{o}</span>
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setFieldForm(p => ({ ...p, options: p.options.filter((_, j) => j !== i) }))}><X className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={optionInput} onChange={e => setOptionInput(e.target.value)} placeholder="Neue Option..." onKeyDown={e => e.key === 'Enter' && addOption()} className="text-sm" />
                  <Button size="sm" onClick={addOption}><Plus className="w-3 h-3" /></Button>
                </div>
              </div>
            )}

            {/* Conditional Logic */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label className="text-sm">Bedingte Anzeige</Label>
                  <p className="text-xs text-gray-400">Feld nur anzeigen wenn alle Bedingungen erfüllt sind</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => setFieldForm(p => ({ ...p, conditions: [...(p.conditions || []), { field_id: '', operator: 'equals', value: '' }] }))} data-testid="add-condition-btn">
                  <Plus className="w-3 h-3 mr-1" /> Bedingung
                </Button>
              </div>
              {(fieldForm.conditions || []).length === 0 ? (
                <p className="text-xs text-gray-400 italic">Keine Bedingungen — Feld immer sichtbar</p>
              ) : (
                <div className="space-y-2">
                  {(fieldForm.conditions || []).map((c, ci) => {
                    const otherFields = fields.filter((f, i) => editingField !== i);
                    const selectedField = otherFields.find(f => f.id === c.field_id);
                    return (
                      <div key={ci} className="flex flex-wrap items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs" data-testid={`condition-row-${ci}`}>
                        <Select value={c.field_id || '_none'} onValueChange={v => setFieldForm(p => ({ ...p, conditions: p.conditions.map((x, i) => i === ci ? { ...x, field_id: v === '_none' ? '' : v } : x) }))}>
                          <SelectTrigger className="h-7 w-32 text-xs"><SelectValue placeholder="Feld..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">— Feld wählen —</SelectItem>
                            {otherFields.map(f => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Select value={c.operator} onValueChange={v => setFieldForm(p => ({ ...p, conditions: p.conditions.map((x, i) => i === ci ? { ...x, operator: v } : x) }))}>
                          <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="equals">ist gleich</SelectItem>
                            <SelectItem value="not_equals">ist nicht gleich</SelectItem>
                            <SelectItem value="contains">enthält</SelectItem>
                            <SelectItem value="not_empty">ist gesetzt</SelectItem>
                            <SelectItem value="empty">ist leer</SelectItem>
                          </SelectContent>
                        </Select>
                        {!['empty', 'not_empty'].includes(c.operator) && (
                          selectedField && ['dropdown', 'checkbox'].includes(selectedField.type) && (selectedField.options || []).length > 0 ? (
                            <Select value={c.value || '_none'} onValueChange={v => setFieldForm(p => ({ ...p, conditions: p.conditions.map((x, i) => i === ci ? { ...x, value: v === '_none' ? '' : v } : x) }))}>
                              <SelectTrigger className="h-7 w-32 text-xs"><SelectValue placeholder="Wert..." /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">—</SelectItem>
                                {selectedField.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ) : selectedField && selectedField.type === 'yes_no' ? (
                            <Select value={c.value === true || c.value === 'true' ? 'true' : 'false'} onValueChange={v => setFieldForm(p => ({ ...p, conditions: p.conditions.map((x, i) => i === ci ? { ...x, value: v === 'true' } : x) }))}>
                              <SelectTrigger className="h-7 w-24 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="true">Ja</SelectItem>
                                <SelectItem value="false">Nein</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input className="h-7 w-32 text-xs" value={c.value || ''} onChange={e => setFieldForm(p => ({ ...p, conditions: p.conditions.map((x, i) => i === ci ? { ...x, value: e.target.value } : x) }))} placeholder="Wert" />
                          )
                        )}
                        <Button size="icon" variant="ghost" className="h-6 w-6 ml-auto" onClick={() => setFieldForm(p => ({ ...p, conditions: p.conditions.filter((_, i) => i !== ci) }))}>
                          <X className="w-3 h-3 text-red-500" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowFieldDialog(false)}>Abbrechen</Button>
              <Button onClick={saveField} data-testid="save-field-btn"><Save className="w-4 h-4 mr-1" /> Speichern</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* STATUS DIALOG */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent data-testid="status-dialog">
          <DialogHeader><DialogTitle>{editingStatus !== null ? 'Status bearbeiten' : 'Neuer Status'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={statusForm.name} onChange={e => setStatusForm(p => ({ ...p, name: e.target.value }))} data-testid="status-name-input" /></div>
            <div><Label>Kategorie</Label>
              <Select value={statusForm.category} onValueChange={v => setStatusForm(p => ({ ...p, category: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Farbe</Label>
              <div className="flex gap-2">
                <Input type="color" value={statusForm.color} onChange={e => setStatusForm(p => ({ ...p, color: e.target.value }))} className="w-12 h-9 p-0.5" />
                <Input value={statusForm.color} onChange={e => setStatusForm(p => ({ ...p, color: e.target.value }))} className="font-mono text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowStatusDialog(false)}>Abbrechen</Button>
              <Button onClick={saveStatus} data-testid="save-status-btn"><Save className="w-4 h-4 mr-1" /> Speichern</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* WORKFLOW STEP DIALOG */}
      <Dialog open={showStepDialog} onOpenChange={setShowStepDialog}>
        <DialogContent className="max-w-lg" data-testid="step-dialog">
          <DialogHeader>
            <DialogTitle>{editingStep !== null ? 'Freigabe-Schritt bearbeiten' : 'Neuer Freigabe-Schritt'}</DialogTitle>
            <DialogDescription>Wer muss freigeben? Welcher Status wird gesetzt?</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div>
              <Label>Name *</Label>
              <Input value={stepForm.name} onChange={e => setStepForm(p => ({ ...p, name: e.target.value }))} placeholder="z.B. Marketing-Freigabe" data-testid="step-name-input" />
            </div>

            <div>
              <Label>Genehmiger *</Label>
              <p className="text-xs text-gray-400 mb-1.5">Personen, die freigeben dürfen (eine Freigabe genügt)</p>
              <UserMultiSelect
                users={users}
                selectedIds={stepForm.approver_ids}
                onChange={(ids) => setStepForm(p => ({ ...p, approver_ids: ids }))}
                placeholder="Nach Name suchen…"
                testId="step-approvers-select"
                emptyText="Keine Personen gefunden"
              />
              <p className="text-xs text-gray-500 mt-1">{stepForm.approver_ids.length} ausgewählt</p>
            </div>

            <div>
              <Label>Status während der Freigabe (optional)</Label>
              <Select value={stepForm.pending_status_id || '_none'} onValueChange={v => setStepForm(p => ({ ...p, pending_status_id: v === '_none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Status..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Status nicht ändern —</SelectItem>
                  {statuses.map(s => <SelectItem key={s.id} value={s.id}><div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />{s.name}</div></SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Status nach Freigabe (optional)</Label>
              <Select value={stepForm.target_status_id || '_none'} onValueChange={v => setStepForm(p => ({ ...p, target_status_id: v === '_none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Status..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Status nicht ändern —</SelectItem>
                  {statuses.map(s => <SelectItem key={s.id} value={s.id}><div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />{s.name}</div></SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Status bei Ablehnung (optional)</Label>
              <Select value={stepForm.rejection_status_id || '_none'} onValueChange={v => setStepForm(p => ({ ...p, rejection_status_id: v === '_none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Status..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Standard 'Abgelehnt' —</SelectItem>
                  {statuses.map(s => <SelectItem key={s.id} value={s.id}><div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />{s.name}</div></SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setShowStepDialog(false)}>Abbrechen</Button>
              <Button onClick={saveStep} data-testid="save-step-btn"><Save className="w-4 h-4 mr-1" /> Speichern</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAnfragen;
